/**
 * Service HTTP de Execuções.
 *
 * Endpoints cobertos (Orchestrator API §Execution Endpoints):
 *   GET    /api/v1/executions                 → list paginado + filtros server-side
 *   GET    /api/v1/executions/{id}            → detail (inclui duration_s)
 *   GET    /api/v1/executions/{id}/report     → relatório JSON completo
 *   DELETE /api/v1/executions/{id}            → cancelar (queued ou running)
 *
 * Filtros aceitos pelo backend (§11.8 da spec frontend) e mapeados a partir
 * de `ExecutionFilters` (`types/api.ts`):
 *   - status         (queued|running|approved|rejected|undetermined|cancelled|error)
 *   - target_type    (test_case|test_suite)
 *   - started_after  (ISO datetime)
 *   - started_before (ISO datetime)
 *   - triggered_by   (user|schedule)
 *
 * O `X-Tenant-ID` e `Authorization` são injetados pelo interceptor de
 * `api.ts` — este service apenas monta o path + query.
 */

import { api } from '@/services/api'
import { config } from '@/lib/config'
import { PAGE_SIZE } from '@/lib/constants'
import type {
  Execution,
  ExecutionFilters,
  PaginatedResponse,
} from '@/types/api'

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

const BASE = '/api/v1/executions'

const paths = {
  list: BASE,
  detail: (id: string) => `${BASE}/${id}`,
  report: (id: string) => `${BASE}/${id}/report`,
  reportPdf: (id: string) => `${BASE}/${id}/report/pdf`,
  cancel: (id: string) => `${BASE}/${id}`,
} as const

// ---------------------------------------------------------------------------
// Detail / Report response shapes
// ---------------------------------------------------------------------------

/**
 * Detail de execução — `Execution` enriquecido com `duration_s` calculado
 * pelo backend (`ended_at - started_at`) e `target_name` (resolvido à parte
 * pelo frontend quando necessário).
 */
export interface ExecutionDetail extends Execution {
  duration_s: number | null
  target_name: string | null
}

/**
 * Relatório completo de execução. O Orchestrator devolve o JSON gravado em
 * `execution_reports.report_json` (espelhado do Worker) — campos opacos para
 * o frontend, que apenas exibe.
 */
export interface ExecutionReport {
  test_id: string
  test_name: string
  status: string
  duration_s: number
  call?: Record<string, unknown>
  steps?: unknown[]
  transcription_full?: unknown[]
  analysis?: {
    algorithmic?: { score_total?: number; [k: string]: unknown }
    llm?: { score_total?: number; [k: string]: unknown }
    composite?: { score: number; grade: string; [k: string]: unknown }
    [k: string]: unknown
  }
  [k: string]: unknown
}

// ---------------------------------------------------------------------------
// Listagem — params (page + filtros server-side)
// ---------------------------------------------------------------------------

export interface ListExecutionsParams {
  page?: number
  page_size?: number
  filters?: ExecutionFilters
}

/**
 * Constrói o objeto de query params para `GET /executions`.
 * - Inclui apenas campos definidos (omitte `undefined`) para não enviar
 *   `?status=` vazio ao backend.
 * - `started_after` / `started_before` esperam ISO datetime (ex.: o frontend
 *   converte do Calendar Shadcn antes de chamar o service).
 */
function buildListParams(
  page: number,
  pageSize: number,
  filters: ExecutionFilters = {},
): Record<string, unknown> {
  const params: Record<string, unknown> = { page, page_size: pageSize }
  if (filters.status) params.status = filters.status
  if (filters.target_type) params.target_type = filters.target_type
  if (filters.started_after) params.started_after = filters.started_after
  if (filters.started_before) params.started_before = filters.started_before
  if (filters.triggered_by) params.triggered_by = filters.triggered_by
  if (filters.run_group_id) params.run_group_id = filters.run_group_id
  return params
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const executionsService = {
  /**
   * Lista paginada de execuções do tenant ativo, com filtros server-side
   * combinados por AND. Cada filtro definido vira um query param; campos
   * `undefined` são omitidos.
   */
  async list(
    params: ListExecutionsParams = {},
  ): Promise<PaginatedResponse<Execution>> {
    const { page = 1, page_size = PAGE_SIZE, filters = {} } = params
    const { data } = await api.get<PaginatedResponse<Execution>>(paths.list, {
      params: buildListParams(page, page_size, filters),
    })
    return data
  },

  /**
   * Detalhe de uma execução. Permissão `can_view_reports` é verificada no
   * **tenant da execução** (não no `X-Tenant-ID`) — permite cross-tenant.
   */
  async get(id: string): Promise<ExecutionDetail> {
    const { data } = await api.get<ExecutionDetail>(paths.detail(id))
    return data
  },

  /**
   * Relatório JSON completo da execução. Retorna 404 enquanto a execução
   * está em andamento ou se nunca terminou.
   */
  async getReport(id: string): Promise<ExecutionReport> {
    const { data } = await api.get<ExecutionReport | { report_json: ExecutionReport }>(
      paths.report(id),
    )
    // O Orchestrator retorna { execution_id, report_json: {...}, ... }.
    // Extraímos report_json para obter o relatório flat do Worker.
    if ('report_json' in data && data.report_json && typeof data.report_json === 'object') {
      return data.report_json as ExecutionReport
    }
    return data as ExecutionReport
  },

  /**
   * URL absoluta do endpoint de PDF do relatório (Orchestrator, WeasyPrint).
   * Usada em `<a href>`/`window.open` — o token é anexado pelo chamador via
   * `?token=` (download direto não envia header Authorization).
   */
  reportPdfUrl(id: string): string {
    return `${config.apiUrl}${paths.reportPdf(id)}`
  },

  /**
   * Screenshot ao vivo do device (provider itap_agent, execução `running`).
   * Proxy do Orchestrator para o Worker (`GET /test/{id}/screen`). Retorna a
   * imagem JPEG em base64 — 404 quando indisponível.
   */
  async getScreen(
    id: string,
  ): Promise<{ test_id: string; image: string; content_type: string }> {
    const { data } = await api.get<{
      test_id: string
      image: string
      content_type: string
    }>(`${BASE}/${id}/screen`, {
      // Polling ~1.5s: falhas transitórias (404/502) são esperadas e tratadas
      // pelo componente — sem toast global a cada tentativa.
      _skipErrorToast: true,
    })
    return data
  },

  /**
   * Cancela uma execução (`queued` ou `running`). Retorna 409 em estado
   * terminal (approved/rejected/undetermined/error/cancelled). Spec API §"DELETE /executions/{id}".
   *
   * Resposta do backend é 204 No Content — devolvemos `void`.
   */
  async cancel(id: string): Promise<void> {
    await api.delete<void>(paths.cancel(id))
  },
}

export type ExecutionsService = typeof executionsService
